import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger, Button } from '@artstorefronts/ui';
import { Loader2, Check, RotateCcw } from 'lucide-react';
import SuggestionInput from './SuggestionInput';
import { Wand2 } from 'lucide-react';
import { useAnalytics } from '../context/analytics';

interface ContentGenerationPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentValue?: string | null;
  onUse: (value: string) => void;
  onGenerate: (suggestion?: string, previousContent?: string) => Promise<string>;
  placeholder?: string;
  maxLength?: number;
  format?: 'text' | 'html';
  inputField?: string;
}

function ContentGenerationPopover({
  open,
  onOpenChange,
  currentValue,
  onUse,
  onGenerate,
  placeholder,
  maxLength,
  format = 'text',
  inputField,
}: ContentGenerationPopoverProps) {
  const { t } = useTranslation('social');
  const { t: tc } = useTranslation('common');
  const resolvedPlaceholder = placeholder ?? t('content-generation.generating-content');
  const { amplitude, intercom } = useAnalytics();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showSuggestionInput, setShowSuggestionInput] = useState(false);

  const handleGenerate = useCallback(
    async (userSuggestion?: string) => {
      setIsGenerating(true);
      setError(null);
      setGeneratedContent(null);

      const eventProperties = {
        input_field: inputField || 'unknown',
        has_suggestion: !!userSuggestion,
        has_previous_content: !!(generatedContent || currentValue),
      };
      amplitude.track('content_generation_triggered', eventProperties);
      intercom.trackEvent('content_generation_triggered', eventProperties);

      try {
        // When there's a suggestion, pass the current value (or generated content) as previousContent
        // For initial generation, use currentValue. For suggestions, prefer generatedContent if available
        const previousContent = userSuggestion ? generatedContent || currentValue || undefined : undefined;

        const content = await onGenerate(userSuggestion, previousContent);
        if (!content || content.trim().length === 0) {
          throw new Error(t('content-generation.empty-content-error'));
        }
        setGeneratedContent(content);
        setSuggestion('');
        setShowSuggestionInput(false);
      } catch (err: any) {
        const errorMessage = err?.message || t('content-generation.failed-to-generate');
        setError(errorMessage);
        console.error('Generation error:', err);
      } finally {
        setIsGenerating(false);
      }
    },
    [onGenerate, currentValue, generatedContent, amplitude, intercom, inputField]
  );

  const handleUse = useCallback(() => {
    if (generatedContent) {
      onUse(generatedContent);
      setGeneratedContent(null);
      setSuggestion('');
      onOpenChange(false);
    }
  }, [generatedContent, onUse, onOpenChange]);

  const handleRegenerate = useCallback(() => {
    handleGenerate();
  }, [handleGenerate]);

  const handleSubmitSuggestion = useCallback(() => {
    if (suggestion.trim()) {
      handleGenerate(suggestion);
    }
  }, [suggestion, handleGenerate]);

  // Auto-generate when popover opens for the first time
  React.useEffect(() => {
    if (open && !generatedContent && !isGenerating && !error) {
      handleGenerate();
    }
    // Reset state when popover closes
    if (!open) {
      setGeneratedContent(null);
      setSuggestion('');
      setError(null);
      setShowSuggestionInput(false);
    }
  }, [open, handleGenerate, generatedContent, isGenerating, error]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="h-6 px-1" onClick={() => onOpenChange(true)}>
          <Wand2 className="w-4 h-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" className="w-80 p-4 max-h-[500px] flex flex-col" collisionPadding={2}>
        {isGenerating && (
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{resolvedPlaceholder}</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm mb-4">{error}</div>
        )}

        {generatedContent && !isGenerating && (
          <>
            <div className="flex-1 overflow-y-auto mb-4">
              {format === 'html' ? (
                <div
                  className="text-sm text-gray-700 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: generatedContent }}
                />
              ) : (
                <div className="text-sm text-gray-700 whitespace-pre-wrap">{generatedContent}</div>
              )}
              {maxLength && (
                <div className="text-xs text-gray-500 mt-2">
                  {t('content-generation.character-count', { current: generatedContent.length, max: maxLength })}
                  <button
                    type="button"
                    onClick={() => setShowSuggestionInput(!showSuggestionInput)}
                    className="ml-2 text-purple-600 hover:text-purple-700 underline text-xs"
                  >
                    {showSuggestionInput ? tc('cancel') : t('content-generation.suggest-changes')}
                  </button>
                </div>
              )}
              {!maxLength && (
                <div className="text-xs text-gray-500 mt-2">
                  <button
                    type="button"
                    onClick={() => setShowSuggestionInput(!showSuggestionInput)}
                    className="text-purple-600 hover:text-purple-700 underline text-xs"
                  >
                    {showSuggestionInput ? tc('cancel') : t('content-generation.suggest-changes')}
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-3 border-t pt-3">
              {!showSuggestionInput && (
                <div className="flex gap-2 justify-end">
                  <Button
                    size="sm"
                    onClick={handleUse}
                    disabled={maxLength ? generatedContent.length > maxLength : false}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {t('content-generation.use')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleRegenerate} disabled={isGenerating}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {t('content-generation.re-generate')}
                  </Button>
                </div>
              )}

              {showSuggestionInput && (
                <SuggestionInput
                  suggestion={suggestion}
                  setSuggestion={setSuggestion}
                  loading={isGenerating}
                  onSubmitSuggestion={handleSubmitSuggestion}
                  isDisabled={isGenerating}
                  showShadow={false}
                />
              )}
            </div>
          </>
        )}

        {!generatedContent && !isGenerating && !error && (
          <div className="text-sm text-gray-500 text-center py-4">{t('content-generation.click-to-generate')}</div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default ContentGenerationPopover;
